import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useCurrentBusinessEntity,
  useUpdateEmailNotificationFlag,
} from "@/hooks/useBusinessEntityQueries";
import { handleApiError, isNotFoundError } from "@/hooks/useApiQueries";
import { useBreadcrumb } from "@/shared/hooks/useBreadcrumb";
import { getRuntimeConfig } from "@/loadConfig";
import {
  Button,
  ErrorDisplay,
  Loader,
  Modal,
  NoData,
  PageTitle,
} from "@/shared/components";
import { useAuth } from "@/auth";
import { getTypeOfIdentifier } from "@/utils";

export default function BusinessEntityProfilePage() {
  const { t } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumb();
  const { representedEntity } = useAuth();

  const {
    data: businessEntity,
    isLoading,
    error,
    isError,
  } = useCurrentBusinessEntity(!!representedEntity);

  const { mutateAsync: updateEmailNotificationFlag } =
    useUpdateEmailNotificationFlag();

  const config = getRuntimeConfig<Record<string, string>>();
  const bapUrl = config?.["bap-url"];

  const [emailSettings, setEmailSettings] = useState({
    showEmailSettingsModal: false,
    sendEmailNotificationAboutReporting:
      businessEntity?.sendEmailNotificationAboutReporting,
  });

  useEffect(() => {
    if (businessEntity) {
      setEmailSettings(prev => ({
        ...prev,
        sendEmailNotificationAboutReporting:
          businessEntity.sendEmailNotificationAboutReporting,
      }));
    }
  }, [businessEntity]);

  useEffect(() => {
    setBreadcrumbs([{ label: t("breadcrumbs.profile"), path: "/profile" }]);
  }, [setBreadcrumbs, t]);

  const saveEmailSettings = async () => {
    if (
      emailSettings.sendEmailNotificationAboutReporting !=
      businessEntity?.sendEmailNotificationAboutReporting
    ) {
      await updateEmailNotificationFlag(
        !!emailSettings.sendEmailNotificationAboutReporting
      );
    }
    closeEmailSettingsModal();
  };

  const closeEmailSettingsModal = () => {
    setEmailSettings({
      ...emailSettings,
      showEmailSettingsModal: false,
      sendEmailNotificationAboutReporting:
        businessEntity?.sendEmailNotificationAboutReporting,
    });
  };

  if (!representedEntity) {
    return <Loader />;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    if (isNotFoundError(error)) {
      return <NoData message={t("summaryPage.businessEntityNotFound")} />;
    }
    return <ErrorDisplay message={handleApiError(error, t)} />;
  }

  if (!businessEntity) {
    return <NoData />;
  }

  return (
    <div>
      <PageTitle>{t("profilePage.title")}</PageTitle>

      <div className="grid grid-cols-2 gap-y-2 mt-6">
        <div className="grid grid-cols-2 items-center gap-y-2 max-w-2xl mt-6">
          <div>{t("profilePage.fields.title")}:</div>
          <div>{businessEntity.title}</div>

          <div>{t("profilePage.fields.identifierVAT")}:</div>
          <div>{businessEntity.identifierVAT}</div>

          <div>{t("profilePage.fields.businessEntityIdentifier")}:</div>
          <div>{businessEntity.businessEntityIdentifier}</div>

          <div>{t("profilePage.fields.businessEntityIdentifierType")}:</div>
          <div>{getTypeOfIdentifier(representedEntity)}</div>

          <div>{t("profilePage.fields.phone")}:</div>
          <div>{businessEntity.phone || "-"}</div>

          <div>{t("profilePage.fields.email")}:</div>
          <div>{businessEntity.email || "-"}</div>

          <div>{t("profilePage.fields.address")}:</div>
          <div>{businessEntity.address}</div>

          <div>{t("profilePage.fields.zipCode")}:</div>
          <div>{businessEntity.zipCode}</div>
        </div>
        <div className="grid h-fit grid-cols-2 gap-y-2 gap-x-6 max-w-2xl mt-6">
          <div>{t("profilePage.fields.emailNotificationsEnabled")}:</div>
          <div className="content-center">
            {t(
              businessEntity.sendEmailNotificationAboutReporting
                ? "common.yes"
                : "common.no"
            )}
          </div>
        </div>
      </div>

      {emailSettings.showEmailSettingsModal && (
        <Modal
          show={emailSettings.showEmailSettingsModal}
          onHide={() => closeEmailSettingsModal}
          closable={false}
          footer={
            <div className="flex gap-4 w-full">
              <Button variant="primary" size="lg" onClick={saveEmailSettings}>
                {t("common.save")}
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={closeEmailSettingsModal}
              >
                {t("common.close")}
              </Button>
            </div>
          }
        >
          <div className="text-xl font-bold text-gray-900">
            {t("profilePage.emailSettingsModal.headerText")}
          </div>
          <div className="flex items-center gap-4 pt-4 text-gray-900">
            <input
              type="checkbox"
              className="scale-150 p-3 justify-center text-primary border-gray-300 rounded focus:ring-primary"
              checked={emailSettings.sendEmailNotificationAboutReporting}
              onChange={e =>
                setEmailSettings({
                  ...emailSettings,
                  sendEmailNotificationAboutReporting: e.target.checked,
                })
              }
            />
            {t("profilePage.emailSettingsModal.inquiryText")}
          </div>
        </Modal>
      )}

      <div className="mt-8">
        <Button variant="primary" onClick={() => window.open(bapUrl, "_blank")}>
          {t("profilePage.editContacts")}
        </Button>
        <Button
          variant="primary"
          className="ml-3"
          onClick={() =>
            setEmailSettings({
              ...emailSettings,
              showEmailSettingsModal: true,
            })
          }
        >
          {t("profilePage.emailSettings")}
        </Button>
      </div>
    </div>
  );
}
